import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { describeAxiosError } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { useTaxYear } from "@/context/TaxYearContext";
import { TaxYearSelect } from "@/components/TaxYearSelect";
import RequireCompany from "@/components/RequireCompany";
import PageHeader from "@/components/PageHeader";
import { UserCircle, Calculator, Layers3, FileBarChart2, ChevronRight, Sparkles, TrendingDown, CalendarClock, FileUp, Trash2, LineChart, Wheat, Users, Briefcase, Home, FileCheck2, Lock, CloudDownload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

function Inner() {
  const { activeId } = useCompany();
  const { ay, fy, isComputable } = useTaxYear();
  const [profile, setProfile] = useState(null);
  const [computations, setComputations] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!activeId) return;
    (async () => {
      try {
        const [p, c, b] = await Promise.all([
          api.get(`/companies/${activeId}/tax/profile`),
          api.get(`/companies/${activeId}/tax/computations?ay=${ay}`),
          api.get(`/companies/${activeId}/tax/blocks?ay=${ay}`),
        ]);
        setProfile(p.data);
        setComputations(c.data || []);
        setBlocks(b.data || []);
      } catch (err) {
        toast.error(describeAxiosError(err));
      }
    })();
  }, [activeId, ay]);

  const seedBlocks = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/companies/${activeId}/tax/blocks/seed?ay=${ay}`);
      toast.success(`Seeded ${data.seeded} depreciation blocks`);
      const { data: b } = await api.get(`/companies/${activeId}/tax/blocks?ay=${ay}`);
      setBlocks(b);
    } catch (err) {
      toast.error(describeAxiosError(err));
    } finally {
      setBusy(false);
    }
  };

  const tiles = [
    {
      to: "/tax/profile",
      title: "Taxpayer Profile",
      desc: profile
        ? `${profile.taxpayer_type} — ${profile.tax_regime} Regime`
        : "Set taxpayer type, PAN, tax regime (Old / New), and residential status.",
      icon: UserCircle,
      accent: profile ? "bg-emerald-50 text-emerald-700" : "bg-purple-50 text-purple-700",
      badge: profile ? "Configured" : "Not set — start here",
      testid: "tax-tile-profile",
    },
    {
      to: "/tax/depreciation",
      title: "Depreciation — Block of Assets",
      desc: blocks.length
        ? `${blocks.length} blocks configured. Total closing WDV to appear in Balance Sheet.`
        : "10 IT-Act blocks (Building 10%, P&M 15%, Computers 40%, …). Half-year rule automated.",
      icon: Layers3,
      accent: "bg-blue-50 text-blue-700",
      badge: blocks.length ? `${blocks.length} block${blocks.length === 1 ? "" : "s"}` : "Empty",
      testid: "tax-tile-depreciation",
    },
    {
      to: "/tax/losses",
      title: "Loss Carry-Forward Ledger",
      desc: "Track prior-year losses (Business 8y / House Property 8y / Capital 8y / Speculation 4y) available for set-off.",
      icon: TrendingDown,
      accent: "bg-rose-50 text-rose-700",
      badge: "Sec 71/72",
      testid: "tax-tile-losses",
    },
    {
      to: "/tax/advance-tax",
      title: "Advance-Tax Planner",
      desc: "4-installment schedule with actual-payment tracking, 234C / 234B interest auto-computed. Presumptive mode supported.",
      icon: CalendarClock,
      accent: "bg-indigo-50 text-indigo-700",
      badge: "Sec 211",
      testid: "tax-tile-advance-tax",
    },
    {
      to: "/tax/capital-gains",
      title: "Capital Gains Calculator",
      desc: "Every asset class — equity, MF, property, gold, unlisted, crypto, bonds. Upload broker Excel from Zerodha / MO / Groww etc. — AI extracts every transaction.",
      icon: LineChart,
      accent: "bg-emerald-50 text-emerald-700",
      badge: "New",
      testid: "tax-tile-capital-gains",
    },
    {
      to: "/tax/salary",
      title: "Salary Income",
      desc: "Per-employer working paper. Auto-computes HRA exemption (Sec 10(13A)), Sec 16(ia) standard deduction, and Sec 16(iii) professional-tax cap.",
      icon: Briefcase,
      accent: "bg-sky-50 text-sky-700",
      badge: "Sec 15-17",
      testid: "tax-tile-salary",
    },
    {
      to: "/tax/house-property",
      title: "House Property",
      desc: "Per-property GAV → NAV → 30% deduction → Sec 24(b) interest. Handles SOP / LOP / DLO. Enforces New-regime Sec 115BAC(2) restrictions.",
      icon: Home,
      accent: "bg-orange-50 text-orange-700",
      badge: "Sec 22-27",
      testid: "tax-tile-house-property",
    },
    {
      to: "/tax/agri-income",
      title: "Agricultural Income",
      desc: "Log crop / dairy / rent-of-agri-land / nursery receipts. Engine applies partial integration (Rule 8/9) automatically when it triggers.",
      icon: Wheat,
      accent: "bg-lime-50 text-lime-700",
      badge: "Sec 10(1)",
      testid: "tax-tile-agri-income",
    },
    {
      to: "/tax/clubbing",
      title: "Clubbing of Income",
      desc: "Sections 60-64: minor child, spouse asset transfer, HUF property, revocable transfers. Auto-adds to the destination head with Sec 10(32) exemption.",
      icon: Users,
      accent: "bg-fuchsia-50 text-fuchsia-700",
      badge: "Sec 60-64",
      testid: "tax-tile-clubbing",
    },
    {
      to: "/tax/imports",
      title: "Form 26AS · AIS / TIS Imports",
      desc: "Upload PDFs — AI extracts TDS rows & income sources, then pushes them straight into a computation. Reconciles imports against your books.",
      icon: FileUp,
      accent: "bg-teal-50 text-teal-700",
      badge: "AI-Powered",
      testid: "tax-tile-imports",
    },
    {
      to: "/tax/portal",
      title: "Income Tax Portal Sync",
      desc: "Fetch AIS, TIS, Form 26AS, past returns, notices & tax paid via Quicko Connect (OTP consent), then e-file and e-pay — no portal password stored.",
      icon: CloudDownload,
      accent: "bg-sky-50 text-sky-700",
      badge: "New",
      testid: "tax-tile-portal",
    },
    {
      to: "/tax/filing",
      title: "Filing & Payments",
      desc: "ITR-form suggester, return filing-status tracker (ack no., refund / demand) and a Challan-280 log for advance & self-assessment tax.",
      icon: FileCheck2,
      accent: "bg-cyan-50 text-cyan-700",
      badge: "New",
      testid: "tax-tile-filing",
    },
    {
      to: "/tax/computation/new",
      title: "New Tax Computation",
      desc: `Auto-extract from books, adjust additions / deductions, apply Chapter VI-A, compute tax for AY ${ay}.`,
      icon: Calculator,
      accent: "bg-amber-50 text-amber-700",
      badge: `AY ${ay}`,
      testid: "tax-tile-new-computation",
      locked: !isComputable,
    },
  ];

  return (
    <>
      <PageHeader
        title={`Income Tax — AY ${ay}`}
        subtitle={`Offline tax computation working papers. FY ${fy}.`}
        actions={
          <div className="flex items-center gap-2">
            <TaxYearSelect />
            {!blocks.length && isComputable && (
              <Button onClick={seedBlocks} disabled={busy} className="h-8 bg-blue-700 hover:bg-blue-800" data-testid="btn-seed-blocks">
                <Sparkles className="w-4 h-4 mr-1.5" /> {busy ? "Seeding…" : "Seed default blocks"}
              </Button>
            )}
          </div>
        }
      />
      <div className="p-6 space-y-6">
        {!isComputable && (
          <div className="flex items-start gap-3 border border-amber-300 bg-amber-50 rounded p-3.5" data-testid="ay-noncomputable-banner">
            <Lock className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
            <div className="text-sm text-amber-900">
              <span className="font-semibold">Viewing AY {ay} (FY {fy}) — view only.</span>{" "}
              Fresh tax computation for this year isn't supported yet, so any stored records below are read-only.
              You can still log imports and challans. Switch to a computable year to prepare a return.
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="tax-index-grid">
          {tiles.map(({ to, title, desc, icon: Icon, accent, badge, testid, locked }) => (
            <Link
              key={to}
              to={locked ? "#" : to}
              onClick={(e) => { if (locked) { e.preventDefault(); toast.error(`Tax computation for AY ${ay} is not supported yet. Switch to a computable year.`); } }}
              data-testid={testid}
              aria-disabled={locked || undefined}
              className={`group border border-gray-200 bg-white rounded p-5 transition ${locked ? "opacity-60 cursor-not-allowed" : "hover:border-blue-700 hover:shadow-sm"}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className={`w-10 h-10 rounded ${accent} flex items-center justify-center transition`}>
                  <Icon className="w-5 h-5" />
                </div>
                {locked ? (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 uppercase tracking-wider font-medium flex items-center gap-1"><Lock className="w-2.5 h-2.5" /> View only</span>
                ) : badge && <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 uppercase tracking-wider font-medium">{badge}</span>}
              </div>
              <h3 className="font-medium text-gray-900 flex items-center gap-1">
                {title}
                <ChevronRight className="w-3.5 h-3.5 text-gray-400 group-hover:translate-x-0.5 transition" />
              </h3>
              <p className="text-xs text-gray-500 mt-1 leading-relaxed">{desc}</p>
            </Link>
          ))}
        </div>

        {computations.length > 0 && (
          <div className="border border-gray-200 bg-white rounded" data-testid="tax-computations-list">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileBarChart2 className="w-4 h-4 text-gray-500" />
                <h3 className="font-medium text-sm">Saved Computations</h3>
              </div>
              <span className="text-xs text-gray-500">{computations.length} saved</span>
            </div>
            <table className="tx-table">
              <thead>
                <tr>
                  <th>AY</th>
                  <th>Taxpayer</th>
                  <th>Regime</th>
                  <th className="text-right">Total Income</th>
                  <th className="text-right">Tax Payable</th>
                  <th className="text-right">Refund / Payable</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {computations.map((c) => {
                  const p = c.profile_snapshot || {};
                  const cc = c.computed || {};
                  const rp = cc.refund_or_payable || 0;
                  return (
                    <tr key={c.id}>
                      <td className="font-mono text-xs">{c.ay}</td>
                      <td>{p.name || p.taxpayer_type || "—"}</td>
                      <td>{p.tax_regime || "—"}</td>
                      <td className="tx-num">{Number(cc.total_income || 0).toLocaleString("en-IN")}</td>
                      <td className="tx-num">{Number(cc.tax?.total || 0).toLocaleString("en-IN")}</td>
                      <td className={`tx-num font-medium ${rp > 0 ? "text-red-700" : rp < 0 ? "text-emerald-700" : "text-gray-600"}`}>
                        {rp === 0 ? "—" : (rp > 0 ? `${Math.abs(rp).toLocaleString("en-IN")} Payable` : `${Math.abs(rp).toLocaleString("en-IN")} Refund`)}
                      </td>
                      <td className="whitespace-nowrap">
                        <Link to={`/tax/computation/${c.id}`} className="text-blue-700 text-xs hover:underline mr-3" data-testid={`btn-open-${c.id}`}>
                          Open →
                        </Link>
                        <button
                          onClick={async () => {
                            const label = p.name || p.taxpayer_type || "this computation";
                            if (!window.confirm(`Delete tax computation for ${label} (AY ${c.ay})? This cannot be undone.`)) return;
                            try {
                              await api.delete(`/companies/${activeId}/tax/computations/${c.id}`);
                              toast.success("Computation deleted");
                              setComputations((list) => list.filter((r) => r.id !== c.id));
                            } catch (err) {
                              toast.error(describeAxiosError(err));
                            }
                          }}
                          className="text-red-600 hover:text-red-800 inline-flex items-center"
                          data-testid={`btn-delete-computation-${c.id}`}
                          title="Delete this computation"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

export default function TaxDashboard() {
  return <RequireCompany><Inner /></RequireCompany>;
}
